"use client";

import React from "react";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { TagBadge } from "@/components/ui/tag-badge";
import { SessionType } from "@/data/core";
import { Zap } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { TOOLTIP_TRIGGER_FOCUS_BASE_CLASSES } from "@/components/ui/tooltip-trigger-classes";

type SessionTypeBadgeProps = {
  type: SessionType;
  tooltipTriggerProps?: Omit<
    React.ComponentPropsWithoutRef<"button">,
    "children" | "type"
  >;
} & React.ComponentProps<typeof Badge>;

export function SessionTypeBadge({
  type,
  className,
  variant,
  tooltipTriggerProps,
  ...badgeProps
}: SessionTypeBadgeProps) {
  let label: React.ReactNode = type;
  if (type === SessionType.PanelDiscussion) {
    label = "Panel";
  } else if (type === SessionType.LightningTalk) {
    label = (
      <>
        <Zap className="size-4" aria-hidden="true" />
        <span aria-hidden="true">Talk</span>
        <span className="sr-only">Lightning Talk</span>
      </>
    );
  }

  const tooltip =
    type === SessionType.LightningTalk
      ? "Lightning Talk (15 min)"
      : type === SessionType.PanelDiscussion
        ? "Panel discussion"
        : undefined;

  const shouldTooltip = tooltip !== undefined;

  if (!shouldTooltip) {
    // The exact markup TagBadge renders, as a plain span: every session in
    // the events table carries one, and TagBadge plus Badge hydrated two
    // component fibers per badge for output that never varies.
    return (
      <span
        data-slot="badge"
        data-variant={variant ?? "secondary"}
        className={cn(
          badgeVariants({ variant: variant ?? "secondary" }),
          "dark:bg-secondary bg-[rgba(0,0,0,0.05)]",
          className,
        )}
        {...badgeProps}
      >
        {label}
      </span>
    );
  }

  return (
    <Tooltip interaction="toggle">
      <TooltipTrigger asChild>
        <button
          {...tooltipTriggerProps}
          type="button"
          className={cn(
            TOOLTIP_TRIGGER_FOCUS_BASE_CLASSES,
            "rounded-full",
            tooltipTriggerProps?.className,
          )}
          data-testid="session-type-badge-tooltip-trigger"
        >
          <TagBadge
            {...badgeProps}
            className={cn("pointer-events-none", className)}
            variant={variant}
          >
            {label}
          </TagBadge>
        </button>
      </TooltipTrigger>
      <TooltipContent data-testid="session-type-tooltip">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
